import { useRef } from 'react';
import CourseCard from './CourseCard';

interface Course {
  id: string;
  badge: string;
  type: string;
  category: string;
  title: string;
  duration: string;
  modules: number;
  image: string;
  isPrototype?: boolean;
  brand?: string;
  description?: string;
}

interface CourseSectionProps {
  title: string;
  subtitle?: string;
  brand?: string;
  courses: Course[];
}

function PovosDoBrasilHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="relative mb-8 rounded-3xl overflow-hidden">
      {/* Background pattern */}
      <div className="bg-gradient-to-r from-[#C85C2A] via-[#A8461A] to-[#2D6A4F] p-8 md:p-10">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-[#E8A42A]/20 rounded-full -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-16 w-32 h-32 bg-white/10 rounded-full translate-y-1/2"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-[#2D6A4F]/30 rounded-full -translate-y-1/2"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            {/* Brand dots */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#E8A42A]"></div>
              <div className="w-3 h-3 rounded-full bg-white"></div>
              <div className="w-3 h-3 rounded-full bg-[#2D6A4F] border border-white/40"></div>
              <span className="text-white/80 text-xs font-bold tracking-widest uppercase ml-1">Série Especial</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-white/85 text-base md:text-lg max-w-xl">{subtitle}</p>
            )}
          </div>

          {/* "Em desenvolvimento" badge */}
          <div className="flex-shrink-0 bg-white/15 backdrop-blur-sm border border-white/30 rounded-2xl px-5 py-3 text-center">
            <div className="flex items-center gap-2 text-white">
              <i className="ri-draft-line text-xl text-[#E8A42A]"></i>
              <div className="text-left">
                <div className="text-xs text-white/70 font-medium">Status</div>
                <div className="text-sm font-bold whitespace-nowrap">Em desenvolvimento</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom colored strip */}
      <div className="h-1.5 flex">
        <div className="flex-1 bg-[#C85C2A]"></div>
        <div className="flex-1 bg-[#E8A42A]"></div>
        <div className="flex-1 bg-[#2D6A4F]"></div>
        <div className="flex-1 bg-[#E8A42A]"></div>
        <div className="flex-1 bg-[#C85C2A]"></div>
      </div>
    </div>
  );
}

export default function CourseSection({ title, subtitle, brand, courses }: CourseSectionProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  const isPovos = brand === 'povos-do-brasil';

  return (
    <div className={`mb-16 ${isPovos ? 'bg-[#FBF5ED] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-10 rounded-3xl' : ''}`}>
      {isPovos ? (
        <PovosDoBrasilHeader title={title} subtitle={subtitle} />
      ) : (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 flex items-center justify-center bg-white hover:bg-[#0099CC] text-[#0099CC] hover:text-white rounded-full shadow-md transition-all cursor-pointer"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 flex items-center justify-center bg-white hover:bg-[#0099CC] text-[#0099CC] hover:text-white rounded-full shadow-md transition-all cursor-pointer"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        {isPovos && (
          <div className="absolute left-0 top-0 bottom-0 flex items-center z-10 -ml-2">
            <button
              onClick={() => scroll('left')}
              className="w-9 h-9 flex items-center justify-center bg-white hover:bg-[#C85C2A] text-[#C85C2A] hover:text-white rounded-full shadow-md transition-all cursor-pointer border border-[#C85C2A]/20"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="flex gap-5 overflow-x-auto scroll-smooth pb-4 px-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {courses.map((course) => (
            <div key={course.id} className="flex-shrink-0 w-72">
              <CourseCard
                id={course.id}
                title={course.title}
                description={course.description ?? `${course.type} • ${course.category}`}
                duration={course.duration}
                modules={course.modules}
                image={course.image}
                brand={course.brand}
                isPrototype={course.isPrototype}
                category={course.category}
              />
            </div>
          ))}
        </div>

        {isPovos && (
          <div className="absolute right-0 top-0 bottom-0 flex items-center z-10 -mr-2">
            <button
              onClick={() => scroll('right')}
              className="w-9 h-9 flex items-center justify-center bg-white hover:bg-[#C85C2A] text-[#C85C2A] hover:text-white rounded-full shadow-md transition-all cursor-pointer border border-[#C85C2A]/20"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
